import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { requireEnv } from '../../utils/env';
import type { Database } from './types';

const supabaseUrl = requireEnv('VITE_SUPABASE_URL');
const supabaseAnonKey = requireEnv('VITE_SUPABASE_ANON_KEY');

/**
 * Custom storage adapter that supports both persistent and session-only storage
 * - If rememberMe is true: uses localStorage (persists indefinitely)
 * - If rememberMe is false: uses sessionStorage (cleared when browser closes)
 */
const customStorageAdapter = {
  getItem: (key: string) => {
    // Check sessionStorage first (for non-persistent sessions)
    const sessionItem = sessionStorage.getItem(key);
    if (sessionItem) return sessionItem;

    // Fall back to localStorage (for persistent sessions)
    return localStorage.getItem(key);
  },
  setItem: (key: string, value: string) => {
    // Check if user wants persistent session
    const rememberMe = localStorage.getItem('candle-remember-me');

    if (rememberMe === 'false') {
      // Use sessionStorage for non-persistent sessions (cleared on browser close)
      sessionStorage.setItem(key, value);
      // Ensure it's not in localStorage
      localStorage.removeItem(key);
    } else {
      // Use localStorage for persistent sessions (default, persists indefinitely)
      localStorage.setItem(key, value);
      // Ensure it's not in sessionStorage
      sessionStorage.removeItem(key);
    }
  },
  removeItem: (key: string) => {
    // Remove from both storages
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  },
};

/**
 * GoTrue's default Navigator lock throws inside the LockManager callback when
 * auto-refresh cannot grab lock:candle-auth immediately (second tab, signed-in
 * home load racing initialize, or Vite HMR creating another client). Firefox
 * surfaces that as an uncaught promise even though the library catches it later.
 * Skip the tick instead of throwing.
 */
async function webAuthLock<R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks?.request) {
    return await fn();
  }

  const abort = new AbortController();
  const timer =
    acquireTimeout > 0 ? window.setTimeout(() => abort.abort(), acquireTimeout) : undefined;

  type Outcome = { ok: true; value: R } | { ok: false };

  try {
    const outcome = await locks.request(
      name,
      acquireTimeout === 0
        ? { mode: 'exclusive', ifAvailable: true }
        : { mode: 'exclusive', signal: abort.signal },
      async (lock): Promise<Outcome> => {
        if (!lock && acquireTimeout === 0) {
          return { ok: false };
        }
        return { ok: true, value: await fn() };
      },
    );

    if (!outcome.ok) {
      return undefined as R;
    }
    return outcome.value;
  } catch (error) {
    if (abort.signal.aborted) {
      const timeoutError = new Error(`Timed out acquiring auth lock "${name}"`) as Error & {
        isAcquireTimeout: boolean;
      };
      timeoutError.isAcquireTimeout = true;
      throw timeoutError;
    }
    throw error;
  } finally {
    if (timer !== undefined) window.clearTimeout(timer);
  }
}

const globalForSupabase = globalThis as typeof globalThis & {
  __candleWebSupabase?: SupabaseClient<Database>;
};

export const supabase =
  globalForSupabase.__candleWebSupabase ??
  createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storage: customStorageAdapter,
      storageKey: 'candle-auth',
      flowType: 'implicit', // Email confirmation links require implicit flow — PKCE breaks when the link opens in a different browser/tab since the code verifier is missing from storage
      lock: webAuthLock,
    },
    realtime: {
      params: {
        eventsPerSecond: 10,
      },
    },
  });

globalForSupabase.__candleWebSupabase = supabase;

// Auth helpers
export const auth = supabase.auth;
export const db = supabase;
export const storage = supabase.storage;
export const realtime = supabase.realtime;
